import { useAuth } from './useAuth';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin, type PermissionKey, type UserPermissions } from '@/lib/utils/permissions';

/**
 * Hook for checking user permissions
 * Makes it easy to check permissions in any component
 */
export function usePermissions() {
  const { user } = useAuth();
  const [userPerms, setUserPerms] = useState<UserPermissions | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Fetch user's role and permissions from database
  useEffect(() => {
    const loadUserPerms = async () => {
      console.log('[usePermissions] Starting loadUserPerms');
      console.log('[usePermissions] User email:', user?.email);
      
      if (!user?.email) {
        console.log('[usePermissions] No user email, setting userPerms to null');
        setUserPerms(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      
      // Hardcoded admin emails as fallback
      const knownAdminEmails = [
        'george.mcpherson@rightawaygroup.com',
        'jasper.howell@rightawaygroup.com'
      ];
      
      const isKnownAdmin = knownAdminEmails.includes(user.email.toLowerCase());
      console.log('[usePermissions] Is known admin email?', isKnownAdmin);
      console.log('[usePermissions] User email (lowercase):', user.email.toLowerCase());
      
      const { data: appUser, error } = await supabase
        .from('app_users')
        .select('role, permissions')
        .eq('email', user.email)
        .maybeSingle();

      console.log('[usePermissions] Database query result:');
      console.log('[usePermissions] - appUser:', appUser);
      console.log('[usePermissions] - error:', error);

      if (appUser) {
        console.log('[usePermissions] User found in database with role:', appUser.role);
        const perms = {
          role: appUser.role as any,
          permissions: appUser.permissions as Record<string, boolean> | undefined
        };
        console.log('[usePermissions] Setting userPerms to:', perms);
        setUserPerms(perms);
      } else if (isKnownAdmin) {
        // Fallback to hardcoded admin for known admin emails
        console.log('[usePermissions] Using hardcoded admin role for:', user.email);
        const perms = {
          role: 'admin' as any,
          permissions: undefined
        };
        console.log('[usePermissions] Setting userPerms to hardcoded admin:', perms);
        setUserPerms(perms);
      } else {
        // For non-admin users not found in database, default to basic user with no permissions
        console.log('[usePermissions] User not found in database and not known admin, defaulting to basic user');
        const perms = {
          role: 'user' as any,
          permissions: {} as Record<string, boolean>
        };
        console.log('[usePermissions] Setting userPerms to basic user:', perms);
        setUserPerms(perms);
      }

      setIsLoading(false);
    };

    loadUserPerms();
  }, [user?.email]);
  
  const checkPermission = (permission: PermissionKey): boolean => {
    return hasPermission(userPerms, permission);
  };
  
  const checkAnyPermission = (permissions: PermissionKey[]): boolean => {
    return hasAnyPermission(userPerms, permissions);
  };
  
  const checkAllPermissions = (permissions: PermissionKey[]): boolean => {
    return hasAllPermissions(userPerms, permissions);
  };
  
  const checkIsAdmin = (): boolean => {
    const result = isAdmin(userPerms);
    console.log('[usePermissions] checkIsAdmin called, userPerms:', userPerms, 'result:', result);
    return result;
  };
  
  return {
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions,
    isAdmin: checkIsAdmin,
    isLoading,
    userPerms,  // Export this so components can check if permissions have loaded
    user
  };
}
// Debug version with comprehensive logging
